import { CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';

type Status = 'healthy' | 'degraded' | 'down' | 'unknown';

interface SystemService {
  name: string;
  status: Status;
  latency?: number;
  message?: string;
}

interface SystemStatusProps {
  services: SystemService[];
  loading?: boolean;
}

const statusConfig: Record<Status, { icon: typeof CheckCircle2; color: string; label: string }> = {
  healthy: { icon: CheckCircle2, color: 'text-green-400', label: 'Healthy' },
  degraded: { icon: AlertCircle, color: 'text-yellow-400', label: 'Degraded' },
  down: { icon: XCircle, color: 'text-red-400', label: 'Down' },
  unknown: { icon: AlertCircle, color: 'text-slate-400', label: 'Unknown' },
};

export function SystemStatus({ services, loading = false }: SystemStatusProps) {
  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-4">System Status</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      </div>
    );
  }

  const overallStatus = services.every((s) => s.status === 'healthy')
    ? 'healthy'
    : services.some((s) => s.status === 'down')
    ? 'down'
    : 'degraded';

  const OverallIcon = statusConfig[overallStatus].icon;

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">System Status</h3>
        <div className={`flex items-center gap-2 ${statusConfig[overallStatus].color}`}>
          <OverallIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{statusConfig[overallStatus].label}</span>
        </div>
      </div>

      <div className="space-y-3">
        {services.map((service) => {
          const { icon: Icon, color } = statusConfig[service.status];
          return (
            <div
              key={service.name}
              className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0"
            >
              <div className="flex items-center gap-3">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className="text-sm text-white">{service.name}</span>
              </div>
              <div className="flex items-center gap-3">
                {service.latency !== undefined && (
                  <span className="text-xs text-slate-500">{service.latency}ms</span>
                )}
                <span className={`text-xs font-medium ${color}`}>
                  {statusConfig[service.status].label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
